// Phase 2D.1B — does the CURRENT user have platform-admin access?
//
// The answer comes from the backend only (`GET /api/platform-admin/access`),
// which reads `public.platform_admins` with the service-role client. An
// organization role is never consulted, and the request deliberately carries no
// `X-Organization-ID`: platform administration does not belong to a tenant.
//
// The result is cached per user id for the lifetime of the page so that
// navigating between customer screens does not re-ask on every mount. It is
// dropped as soon as the authenticated user changes.
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { reportError } from "../lib/errorReporter";

const cache = { userId: null, checked: false, isPlatformAdmin: false };

export function usePlatformAccess() {
  const { loading: authLoading, session } = useAuth();
  const userId = session?.user?.id || null;
  const [state, setState] = useState(() =>
    cache.userId && cache.userId === userId
      ? { checked: cache.checked, isPlatformAdmin: cache.isPlatformAdmin }
      : { checked: false, isPlatformAdmin: false },
  );

  useEffect(() => {
    if (authLoading) return undefined;
    if (!userId) {
      cache.userId = null;
      cache.checked = false;
      cache.isPlatformAdmin = false;
      setState({ checked: true, isPlatformAdmin: false });
      return undefined;
    }
    if (cache.userId === userId && cache.checked) {
      setState({ checked: true, isPlatformAdmin: cache.isPlatformAdmin });
      return undefined;
    }
    let cancelled = false;
    setState({ checked: false, isPlatformAdmin: false });
    api
      .get("/platform-admin/access", { skipOrgHeader: true })
      .then((r) => {
        // Fail closed: anything other than an explicit `true` is no access.
        const granted = r?.data?.is_platform_admin === true;
        cache.userId = userId;
        cache.checked = true;
        cache.isPlatformAdmin = granted;
        if (!cancelled) setState({ checked: true, isPlatformAdmin: granted });
      })
      .catch((err) => {
        reportError("platform.access", err);
        cache.userId = userId;
        cache.checked = true;
        cache.isPlatformAdmin = false;
        if (!cancelled) setState({ checked: true, isPlatformAdmin: false });
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, userId]);

  return {
    loading: authLoading || !state.checked,
    isPlatformAdmin: state.isPlatformAdmin,
  };
}

export default usePlatformAccess;
